import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useClientTeam } from "@/features/clients/hooks/use-clients";
import type { CallDestination } from "@/features/connectors/types";
import { useAssignableDevices } from "@/features/devices/hooks/use-devices";
import { useTranslation } from "@/features/utils/i18n";
import { useParams } from "react-router-dom";
import type { CustomFieldProps } from "./dynamic-form";

const AUTO = "auto";

/** The Select speaks strings; the setting stores a destination. */
const encode = (value: unknown): string => {
  const destination = value as CallDestination | null | undefined;
  if (destination?.kind === "device") return `device:${destination.device_id}`;
  if (destination?.kind === "member") return `member:${destination.member_id}`;
  return AUTO;
};

const decode = (value: string): CallDestination | null => {
  const [kind, id] = [
    value.slice(0, value.indexOf(":")),
    value.slice(value.indexOf(":") + 1),
  ];
  if (kind === "device") return { kind: "device", device_id: id };
  if (kind === "member") return { kind: "member", member_id: id };
  return null;
};

/**
 * The company's main phone (`basic_tools.main_destination`): the one that rings
 * first, by default, when a call goes to the team — a handset, or a team member
 * (their handset, else their mobile). A VIP contact rings this one only.
 * "Automatic" stores nothing, and the server picks the first available phone, a
 * VoIP handset before a mobile.
 *
 * A company setting only: who is calling is known before which agent answers,
 * so an agent's override would never be read.
 */
export function MainPhoneField({
  fieldName,
  fieldSchema,
  value,
  onChange,
  inheritable,
}: CustomFieldProps) {
  const { t } = useTranslation("tools");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: devices = [] } = useAssignableDevices(clientId || "");
  const { data: team } = useClientTeam(clientId || "");

  const base = "tools.catalog.basic_tools.fields.main_destination";
  const label = fieldSchema.title || fieldName;

  if (inheritable) {
    return (
      <div className="space-y-1">
        <Label>{label}</Label>
        <p className="text-sm text-muted-foreground">
          {t(`${base}.companyOnly`)}
        </p>
      </div>
    );
  }

  const members = (team?.members || []).filter(
    (m) => m.membership?.phone || m.membership?.device_id,
  );
  const current = encode(value);
  const known =
    current === AUTO ||
    devices.some((d) => `device:${d.id}` === current) ||
    members.some((m) => `member:${m.membership.id}` === current);

  return (
    <div className="space-y-2">
      <Label htmlFor={fieldName}>{label}</Label>
      <Select
        value={current}
        onValueChange={(next) => onChange(decode(next))}
      >
        <SelectTrigger id={fieldName} className="md:w-96">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={AUTO}>{t(`${base}.auto`)}</SelectItem>
          {!known && (
            <SelectItem value={current}>{t(`${base}.unknown`)}</SelectItem>
          )}
          {devices.length > 0 && (
            <SelectGroup>
              <SelectLabel>{t(`${base}.devices`)}</SelectLabel>
              {devices.map((device) => (
                <SelectItem key={device.id} value={`device:${device.id}`}>
                  {device.label || device.id}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
          {members.length > 0 && (
            <SelectGroup>
              <SelectLabel>{t(`${base}.members`)}</SelectLabel>
              {members.map((m) => (
                <SelectItem
                  key={m.membership.id}
                  value={`member:${m.membership.id}`}
                >
                  {m.membership.display_name ||
                    m.user?.full_name ||
                    m.user?.email ||
                    m.membership.phone}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>
      {fieldSchema.description && (
        <p className="text-sm text-muted-foreground">
          {fieldSchema.description}
        </p>
      )}
    </div>
  );
}
